import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateModule } from '@ngx-translate/core';
import { ITask } from '@nimbus-assistant/models/task.model';
import { Channel, Modality } from '@nimbus-assistant/models/task-types.model';
import { AssistantTasksService } from '@nimbus-assistant/services/assistant-tasks.service';
import { SettingsService } from '@nimbus-assistant/services/settings.service';
import { MessageService } from '@nimbus-assistant/services/message.service';
import { OpenInBrowserMessage } from '@nimbus-assistant/models/web-view-messages/open-in-browser-message';
import { SharedUIModule } from '@nimbus-library/features/shared/shared.ui.module';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { Icon, ICONS } from '@nimbus-library/features/shared/resources/icons';
import { IconWeight } from '@nimbus-library/features/shared/directives/icon-weight.enum';

export enum TaskActionsMode {
  Incoming = 'incoming',
  Connected = 'connected',
  Parked = 'parked',
}

@UntilDestroy()
@Component({
  standalone: true,
  selector: 'app-footer-actions',
  templateUrl: './footer-actions.component.html',
  styleUrls: ['./footer-actions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslateModule, SharedUIModule],
})
export class FooterActionsComponent {
  @Input() public task: ITask;
  @Input() public mode: TaskActionsMode = TaskActionsMode.Connected;

  public readonly ICONS = ICONS;
  public readonly IconWeight = IconWeight;
  public readonly TaskActionsMode = TaskActionsMode;
  public readonly TranslationKeys = TranslationKeys;
  public actionInProgress = false;

  public get isAudioTask(): boolean {
    return this.task?.modality === Modality.Audio;
  }

  public get acceptIcon(): Icon {
    return this.isAudioTask ? ICONS.PHONE : ICONS.CHECK;
  }

  public get declineIcon(): Icon {
    return this.isAudioTask ? ICONS.DECLINE : ICONS.CLOSE;
  }

  public get declineLabel(): TranslationKeys {
    return this.isAudioTask ? TranslationKeys.GeneralDecline : TranslationKeys.GeneralReject;
  }

  public get showJoinConversation(): boolean {
    return [Modality.IM, Modality.Email].includes(this.task?.modality);
  }

  public get showCloseTask(): boolean {
    return this.task?.modality !== Modality.Email && !this.task?.isDirectCall;
  }

  public constructor(
    private readonly tasksService: AssistantTasksService,
    private readonly settingsService: SettingsService,
    private readonly cd: ChangeDetectorRef,
  ) {}

  public onAccept(): void {
    if (!this.task || this.actionInProgress) {
      return;
    }
    this.actionInProgress = true;
    this.tasksService
      .acceptTask(this.task.unifiedConversationId, this.task.conversationId, this.task.modality)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.actionInProgress = false;
        this.cd.markForCheck();
      });
  }

  public onDecline(): void {
    if (!this.task || this.actionInProgress) {
      return;
    }
    this.actionInProgress = true;
    this.tasksService
      .declineTask(this.task.unifiedConversationId, this.task.conversationId, this.task.modality)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.actionInProgress = false;
        this.cd.markForCheck();
      });
  }

  public onCloseTask(): void {
    if (!this.task || this.actionInProgress) {
      return;
    }
    this.actionInProgress = true;
    this.tasksService
      .terminateTask(this.task.unifiedConversationId, this.task.conversationId, this.task.modality)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.actionInProgress = false;
        this.cd.markForCheck();
      });
  }

  public onJoinConversation(): void {
    const isTwilioChannel = [Channel.TwilioWhatsapp, Channel.TwilioSms, Channel.TwilioRcs].includes(this.task?.channel);
    const url = isTwilioChannel || !this.task?.agentChatDeepLink ? this.settingsService.portalSessionsUrl : this.task.agentChatDeepLink;
    MessageService.SendOpenInBrowser(new OpenInBrowserMessage(url));
  }

  public onOpenMySessions(): void {
    MessageService.SendOpenInBrowser(new OpenInBrowserMessage(this.settingsService.portalSessionsUrl));
  }
}
